import { Fragment, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { format } from "date-fns";
import { Boxes, Package, Layers, ChevronDown, ChevronRight, Wrench, Eye, EyeOff, Building2, ShoppingCart, PackageCheck, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DetailActions, EmailHistory } from "../components/DetailActions";
import { mediaUrl } from "./ProductsPage";

const ALL = "__all__";

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-stone-200 bg-white p-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{label}</p>
      <Icon size={14} className="text-[#1C4E35]" />
    </div>
    <p className="mt-1 font-heading text-xl font-black text-[#0A0D0B]">{value}</p>
  </div>
);

const farkOf = (p) => {
  const acc = p.acceptedQty || 0;
  const proc = p.procuredQty || 0;
  if (acc > 0) return acc - (p.qty || 0);
  if (proc > 0) return proc - (p.qty || 0);
  return null;
};

const FarkBadge = ({ diff }) => {
  if (diff === null || diff === undefined) return <span className="text-[11px] text-stone-400">—</span>;
  return (
    <span className={`border px-1.5 py-0.5 text-[10px] font-medium ${diff === 0 ? "border-green-300 bg-green-50 text-green-700" : diff < 0 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
      {diff === 0 ? "Tam ✓" : diff < 0 ? `Eksik ${-diff}` : `Fazla +${diff}`}
    </span>
  );
};

export default function ReportsPage() {
  const { user } = useAuth();
  const [scope, setScope] = useState("ordered");
  const [kind, setKind] = useState("__all__");
  const [category, setCategory] = useState(ALL);
  const [orderedGroups, setOrderedGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [hiddenCats, setHiddenCats] = useState([]);
  const [hiddenProds, setHiddenProds] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [mode, setMode] = useState("category");
  const [onlyEksik, setOnlyEksik] = useState(false);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState(null);
  const [custDetails, setCustDetails] = useState({});
  const [openCust, setOpenCust] = useState({});

  useEffect(() => {
    if (mode !== "customer") return;
    setCustomers(null);
    api.get("/orders/customer/overview", { params: { kind: kind !== ALL ? kind : undefined } })
      .then((r) => setCustomers(r.data.data))
      .catch(() => toast.error("Müşteri verileri yüklenemedi"));
  }, [mode, kind]);

  useEffect(() => {
    Promise.all([api.get("/orders/by-product"), api.get("/products"), api.get("/settings/report-hidden")])
      .then(([r1, r2, r3]) => {
        setOrderedGroups(r1.data.data);
        setProducts(r2.data.data);
        setHiddenCats(r3.data.categories);
        setHiddenProds(r3.data.products);
      })
      .catch(() => toast.error("Veriler yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const kindOk = (k) => kind === "__all__" || k === kind;
    const withHidden = (list) => list.map((p) => ({ ...p, hidden: hiddenProds.includes(p.sku) }));
    const summarize = (ps) => {
      const act = ps.filter((p) => !p.hidden);
      const farks = act.map(farkOf).filter((f) => f !== null);
      return {
        count: act.length,
        qty: act.reduce((a, p) => a + (p.qty || 0), 0),
        proc: act.reduce((a, p) => a + (p.procuredQty || 0), 0),
        acc: act.reduce((a, p) => a + (p.acceptedQty || 0), 0),
        fark: farks.reduce((a, f) => a + f, 0),
        hasFark: farks.length > 0,
      };
    };
    let result;
    if (scope === "ordered") {
      result = orderedGroups
        .map((g) => {
          const ps = withHidden(g.products.filter((p) => kindOk(p.kind)).map((p) => ({ sku: p.sku, name: p.name, variant: p.variant, kind: p.kind, qty: p.qty, orderCount: p.orderCount, image: p.image, procuredQty: p.procuredQty || 0, acceptedQty: p.acceptedQty || 0, manualQty: p.manualQty || 0 })));
          return { category: g.category, products: ps, ...summarize(ps) };
        })
        .filter((r) => r.products.length > 0);
    } else {
      const qtyBySku = {};
      const countBySku = {};
      const procBySku = {};
      const accBySku = {};
      orderedGroups.forEach((g) => g.products.forEach((p) => {
        qtyBySku[p.sku] = (qtyBySku[p.sku] || 0) + p.qty;
        countBySku[p.sku] = (countBySku[p.sku] || 0) + (p.orderCount || 0);
        procBySku[p.sku] = (procBySku[p.sku] || 0) + (p.procuredQty || 0);
        accBySku[p.sku] = (accBySku[p.sku] || 0) + (p.acceptedQty || 0);
      }));
      const m = {};
      products.filter((p) => !p.retired && kindOk(["service", "depo_disi"].includes(p.kind) ? p.kind : "product")).forEach((p) => {
        const c = p.category || "Diğer";
        m[c] = m[c] || { category: c, products: [] };
        m[c].products.push({ sku: p.sku, name: p.name, kind: ["service", "depo_disi"].includes(p.kind) ? p.kind : "product", qty: qtyBySku[p.sku] || 0, orderCount: countBySku[p.sku] || 0, image: (p.images || [])[0], procuredQty: procBySku[p.sku] || 0, acceptedQty: accBySku[p.sku] || 0 });
      });
      result = Object.values(m)
        .map((r) => {
          const ps = withHidden(r.products.sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")));
          return { category: r.category, products: ps, ...summarize(ps) };
        })
        .sort((a, b) => a.category.localeCompare(b.category, "tr"));
    }
    return result.map((r) => ({ ...r, hidden: hiddenCats.includes(r.category) }));
  }, [scope, kind, orderedGroups, products, hiddenCats, hiddenProds]);

  if (user && user.role !== "admin" && user.role !== "user") {
    return <Navigate to="/" replace />;
  }

  const q = search.trim().toLowerCase();
  const visible = (category === ALL ? rows : rows.filter((r) => r.category === category))
    .filter((r) => showHidden || !r.hidden)
    .map((r) => (showHidden ? r : { ...r, products: r.products.filter((p) => !p.hidden) }))
    .map((r) => {
      if (!q || r.category.toLowerCase().includes(q)) return r;
      const ps = r.products.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
      const act = ps.filter((p) => !p.hidden);
      const farks = act.map(farkOf).filter((f) => f !== null);
      return { ...r, products: ps, count: act.length, qty: act.reduce((a, p) => a + (p.qty || 0), 0), proc: act.reduce((a, p) => a + (p.procuredQty || 0), 0), acc: act.reduce((a, p) => a + (p.acceptedQty || 0), 0), fark: farks.reduce((a, f) => a + f, 0), hasFark: farks.length > 0 };
    })
    .map((r) => {
      if (!onlyEksik) return r;
      const ps = r.products.filter((p) => {
        const f = farkOf(p);
        return f !== null && f < 0;
      });
      const act = ps.filter((p) => !p.hidden);
      const farks = act.map(farkOf).filter((f) => f !== null);
      return { ...r, products: ps, count: act.length, qty: act.reduce((a, p) => a + (p.qty || 0), 0), proc: act.reduce((a, p) => a + (p.procuredQty || 0), 0), acc: act.reduce((a, p) => a + (p.acceptedQty || 0), 0), fark: farks.reduce((a, f) => a + f, 0), hasFark: farks.length > 0 };
    })
    .filter((r) => r.products.length > 0);
  const visibleCustomers = q ? (customers || []).filter((c) => (c.organisation || "").toLowerCase().includes(q)) : customers;
  const hiddenCount = hiddenCats.length + hiddenProds.length;
  const totals = visible.filter((r) => !r.hidden).reduce((a, r) => ({ count: a.count + r.count, qty: a.qty + r.qty, proc: a.proc + (r.proc || 0), acc: a.acc + (r.acc || 0), fark: a.fark + (r.fark || 0), hasFark: a.hasFark || !!r.hasFark }), { count: 0, qty: 0, proc: 0, acc: 0, fark: 0, hasFark: false });
  const ts = format(new Date(), "yyyyMMdd_HHmm");
  const mainParams = { scope, category: category !== ALL ? category : undefined, kind: kind !== ALL ? kind : undefined, only_eksik: onlyEksik || undefined };
  const isAdmin = user?.role === "admin";

  const toggleHidden = async (type, key, hidden) => {
    try {
      await api.post("/settings/report-hidden", { type, key, hidden });
      if (type === "category") setHiddenCats((l) => (hidden ? [...l, key] : l.filter((x) => x !== key)));
      else setHiddenProds((l) => (hidden ? [...l, key] : l.filter((x) => x !== key)));
      toast.success(hidden ? "Rapordan gizlendi" : "Tekrar görünür yapıldı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const toggleCust = (org) => {
    setOpenCust((o) => ({ ...o, [org]: !o[org] }));
    if (!custDetails[org]) {
      api.get("/orders/by-product", { params: { organisation: org } })
        .then((r) => {
          const prods = r.data.data.flatMap((g) =>
            g.products.map((p) => ({ ...p, category: g.category, hidden: hiddenProds.includes(p.sku) || hiddenCats.includes(g.category) }))
          );
          setCustDetails((d) => ({ ...d, [org]: prods }));
        })
        .catch(() => toast.error("Müşteri detayı yüklenemedi"));
    }
  };

  if (loading) return <p className="text-sm text-stone-500" data-testid="reports-loading">Yükleniyor...</p>;

  return (
    <div className="space-y-5 fade-up" data-testid="reports-page">
      <div className="flex flex-wrap items-end gap-3 border border-stone-200 bg-white p-4">
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Rapor Türü</p>
          <div className="flex border border-stone-300">
            {[["category", "Kategori Bazlı"], ["customer", "Müşteri Bazlı"]].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setMode(val)}
                data-testid={`report-mode-${val}`}
                className={`px-3 py-2 text-xs font-medium transition-colors ${mode === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {mode === "category" && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Kapsam</p>
          <div className="flex border border-stone-300">
            <button
              type="button"
              onClick={() => setScope("ordered")}
              data-testid="report-scope-ordered"
              className={`px-3 py-2 text-xs font-medium transition-colors ${scope === "ordered" ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
            >
              Sadece Sipariş Edilenler
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              data-testid="report-scope-all"
              className={`px-3 py-2 text-xs font-medium transition-colors ${scope === "all" ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
            >
              Tüm Ürünler (sipariş 0 dahil)
            </button>
          </div>
        </div>
        )}
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Tür</p>
          <div className="flex border border-stone-300">
            {[["__all__", "Tümü"], ["product", "Sadece Ürünler"], ["service", "Sadece Servisler"], ["depo_disi", "Depo Dışı"]].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setKind(val)}
                data-testid={`report-kind-${val === "__all__" ? "all" : val}`}
                className={`px-3 py-2 text-xs font-medium transition-colors ${kind === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {mode === "category" && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Kategori</p>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[260px] rounded-sm border-stone-300" data-testid="report-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm Kategoriler</SelectItem>
              {rows.map((r) => (
                <SelectItem key={r.category} value={r.category}>{r.category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Arama</p>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={mode === "customer" ? "Müşteri ara..." : "Ürün adı, SKU veya kategori ara..."}
              data-testid="report-search"
              className="h-9 w-64 rounded-sm border-stone-300 pl-8 text-xs"
            />
          </div>
        </div>
        {mode === "category" && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Fark</p>
          <button
            type="button"
            onClick={() => setOnlyEksik((v) => !v)}
            data-testid="report-only-eksik-toggle"
            className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-medium transition-colors ${onlyEksik ? "border-red-600 bg-red-600 text-white" : "border-stone-300 bg-white text-stone-600 hover:bg-stone-100"}`}
          >
            Sadece Eksikler
          </button>
        </div>
        )}
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Gizlenenler</p>
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            data-testid="report-show-hidden-toggle"
            className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-medium transition-colors ${showHidden ? "border-[#1C4E35] bg-[#1C4E35] text-white" : "border-stone-300 bg-white text-stone-600 hover:bg-stone-100"}`}
          >
            {showHidden ? <Eye size={13} /> : <EyeOff size={13} />}
            {showHidden ? "Ekranda gösteriliyor" : "Ekranda gizli"}
            {hiddenCount > 0 && <span className={`px-1 text-[10px] font-bold ${showHidden ? "text-white" : "text-[#1C4E35]"}`}>({hiddenCount})</span>}
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2 border border-stone-300 bg-stone-50 py-1 pl-2 pr-1" data-testid="report-main-actions">
          <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-stone-500">Resimli Rapor</span>
          <DetailActions
            compact
            pdfPath="/orders/by-product/visual-pdf"
            pdfParams={mainParams}
            emailPath="/orders/by-product/visual-email"
            emailPayload={mainParams}
            filename={`cop31_rapor_${scope}_${ts}.pdf`}
            testPrefix="report-main"
            historyScope="byproduct-visual"
            showMode={false}
          />
        </div>
      </div>

      <div className={`grid grid-cols-2 gap-2 ${mode === "customer" ? "sm:grid-cols-3 lg:grid-cols-5" : "sm:grid-cols-3 lg:grid-cols-6"}`}>
        {mode === "customer" ? (
          <>
            <Kpi icon={Building2} label="Müşteri" value={(visibleCustomers || []).length} testId="report-kpi-customers" />
            <Kpi icon={Package} label="Ürün" value={(visibleCustomers || []).reduce((a, c) => a + c.productCount, 0)} testId="report-kpi-products" />
            <Kpi icon={Boxes} label="Talep Adeti" value={(visibleCustomers || []).reduce((a, c) => a + c.qty, 0)} testId="report-kpi-qty" />
            <Kpi icon={ShoppingCart} label="Satınalma" value={(visibleCustomers || []).reduce((a, c) => a + (c.procuredQty || 0), 0)} testId="report-kpi-cust-procured" />
            <Kpi icon={PackageCheck} label="Mal Kabul" value={(visibleCustomers || []).reduce((a, c) => a + (c.acceptedQty || 0), 0)} testId="report-kpi-cust-accepted" />
          </>
        ) : (
          <>
            <Kpi icon={Layers} label="Kategori" value={visible.length} testId="report-kpi-categories" />
            <Kpi icon={Package} label="Ürün" value={totals.count} testId="report-kpi-products" />
            <Kpi icon={Boxes} label="Talep Adeti" value={totals.qty} testId="report-kpi-qty" />
            <Kpi icon={ShoppingCart} label="Satınalma" value={totals.proc} testId="report-kpi-procured" />
            <Kpi icon={PackageCheck} label="Mal Kabul" value={totals.acc} testId="report-kpi-accepted" />
            <Kpi icon={Boxes} label="Fark" value={totals.hasFark ? (totals.fark > 0 ? `+${totals.fark}` : totals.fark) : "—"} testId="report-kpi-diff" />
          </>
        )}
      </div>

      {mode === "customer" ? (
        <div className="border border-stone-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-xs">Müşteri</TableHead>
                <TableHead className="text-right text-xs">Ürün Sayısı</TableHead>
                <TableHead className="text-right text-xs">Talep Adeti</TableHead>
                <TableHead className="text-right text-xs">Satınalma</TableHead>
                <TableHead className="text-right text-xs" title="Mal kabul depo bazındadır; müşterinin sipariş ettiği ürünlerin toplam depo girişini gösterir">Mal Kabul</TableHead>
                <TableHead className="text-right text-xs">Rapor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers === null && (
                <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-stone-400">Yükleniyor...</TableCell></TableRow>
              )}
              {customers !== null && (visibleCustomers || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-stone-400" data-testid="customer-report-empty">Müşteri bulunamadı.</TableCell></TableRow>
              )}
              {(visibleCustomers || []).map((c) => (
                <Fragment key={c.organisation}>
                  <TableRow
                    data-testid={`customer-report-row-${c.organisation}`}
                    onClick={() => toggleCust(c.organisation)}
                    className="cursor-pointer hover:bg-stone-50"
                  >
                    <TableCell className="py-2.5 text-sm font-medium text-[#0A0D0B]">
                      <div className="flex items-center gap-2">
                        {openCust[c.organisation] ? <ChevronDown size={14} className="text-stone-400" /> : <ChevronRight size={14} className="text-stone-400" />}
                        {c.organisation}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-right text-sm">{c.productCount}</TableCell>
                    <TableCell className="py-2.5 text-right text-sm">{c.qty}</TableCell>
                    <TableCell className={`py-2.5 text-right text-sm ${(c.procuredQty || 0) > 0 ? "text-blue-700" : "text-stone-400"}`} data-testid={`customer-report-proc-${c.organisation}`}>{c.procuredQty || 0}</TableCell>
                    <TableCell className={`py-2.5 text-right text-sm ${(c.acceptedQty || 0) > 0 ? "text-green-700" : "text-stone-400"}`} data-testid={`customer-report-acc-${c.organisation}`}>{c.acceptedQty || 0}</TableCell>
                    <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end">
                        <DetailActions
                          compact
                          pdfPath="/orders/by-product/visual-pdf"
                          pdfParams={{ scope: "ordered", organisation: c.organisation, kind: kind !== ALL ? kind : undefined }}
                          emailPath="/orders/by-product/visual-email"
                          emailPayload={{ scope: "ordered", organisation: c.organisation, kind: kind !== ALL ? kind : undefined }}
                          filename={`cop31_musteri_${c.organisation.replace(/\s+/g, "_")}_${ts}.pdf`}
                          testPrefix={`customer-report-${c.organisation.replace(/\s+/g, "_")}`}
                          historyScope="byproduct-visual"
                          showMode={false}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  {openCust[c.organisation] && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-stone-50 p-0">
                        {!custDetails[c.organisation] ? (
                          <p className="px-8 py-3 text-xs text-stone-400">Yükleniyor...</p>
                        ) : (
                          <ul className="divide-y divide-stone-200" data-testid={`customer-report-detail-${c.organisation}`}>
                            {custDetails[c.organisation]
                              .filter((p) => (kind === ALL || p.kind === kind) && (showHidden || !p.hidden))
                              .map((p) => (
                                <li key={`${p.sku}|${p.variant || ""}`} className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-8 py-2 ${p.hidden ? "opacity-45" : ""}`}>
                                  {p.image ? (
                                    <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="h-12 w-12 shrink-0 border border-stone-200 bg-white object-contain" />
                                  ) : (
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-stone-200 bg-white">
                                      {p.kind === "service" ? <Wrench size={14} className="text-sky-500" /> : <Package size={14} className="text-stone-300" />}
                                    </div>
                                  )}
                                  <div className="min-w-56">
                                    <p className="text-xs font-medium text-[#0A0D0B]">
                                      {p.name}
                                      {p.variant && <span className="ml-1.5 border border-stone-300 bg-white px-1 py-px text-[9px] text-stone-500">{p.variant}</span>}
                                      {(p.manualQty || 0) > 0 && <span className="ml-1.5 border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title="Manuel ekleme içerir">M</span>}
                                    </p>
                                    <p className="text-[10px] text-stone-400">{p.sku} · {p.category}</p>
                                  </div>
                                  {p.kind === "service" && <span className="border border-sky-300 bg-sky-50 px-1.5 py-px text-[9px] font-medium text-sky-700">Servis</span>}
                                  {p.kind === "depo_disi" && <span className="border border-orange-300 bg-orange-50 px-1.5 py-px text-[9px] font-medium text-orange-700">Depo Dışı</span>}
                                  {p.hidden && <span className="border border-stone-300 bg-stone-100 px-1.5 py-px text-[9px] font-medium text-stone-500">Raporda gizli</span>}
                                  <span className="ml-auto text-[11px] text-stone-600">Adet: <strong>{p.qty}</strong></span>
                                  <span className="text-[11px] text-stone-500">Sipariş: {p.orderCount}</span>
                                  <span className={`text-[11px] ${(p.procuredQty || 0) > 0 ? "text-blue-700" : "text-stone-400"}`}>SA: <strong>{p.procuredQty || 0}</strong></span>
                                  <span className={`text-[11px] ${(p.acceptedQty || 0) > 0 ? "text-green-700" : "text-stone-400"}`}>MK: <strong>{p.acceptedQty || 0}</strong></span>
                                </li>
                              ))}
                          </ul>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
      <div className="border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs">Kategori</TableHead>
              <TableHead className="text-right text-xs">Ürün Sayısı</TableHead>
              <TableHead className="text-right text-xs">Talep Adeti</TableHead>
              <TableHead className="text-right text-xs">Satınalma</TableHead>
              <TableHead className="text-right text-xs">Mal Kabul</TableHead>
              <TableHead className="text-right text-xs">Fark</TableHead>
              <TableHead className="text-right text-xs">Rapor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-stone-400" data-testid="report-empty">
                  Gösterilecek kategori bulunamadı.
                </TableCell>
              </TableRow>
            )}
            {visible.map((r) => {
              const isOpen = open[r.category] || (q && !r.category.toLowerCase().includes(q)) || onlyEksik;
              return (
              <Fragment key={r.category}>
                <TableRow
                  data-testid={`report-row-${r.category}`}
                  onClick={() => setOpen((o) => ({ ...o, [r.category]: !o[r.category] }))}
                  className={`cursor-pointer hover:bg-stone-50 ${r.hidden ? "opacity-45" : ""}`}
                >
                  <TableCell className="py-2.5 text-sm font-medium text-[#0A0D0B]">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown size={14} className="text-stone-400" /> : <ChevronRight size={14} className="text-stone-400" />}
                      {r.category}
                      {r.hidden && <span className="border border-stone-300 bg-stone-100 px-1.5 py-px text-[9px] font-medium text-stone-500" data-testid={`report-hidden-badge-${r.category}`}>Raporda gizli</span>}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-sm">{r.count}</TableCell>
                  <TableCell className={`py-2.5 text-right text-sm ${r.qty === 0 ? "text-stone-400" : ""}`}>{r.qty}</TableCell>
                  <TableCell className={`py-2.5 text-right text-sm ${r.proc > 0 ? "text-blue-700" : "text-stone-400"}`} data-testid={`report-proc-${r.category}`}>{r.proc}</TableCell>
                  <TableCell className={`py-2.5 text-right text-sm ${r.acc > 0 ? "text-green-700" : "text-stone-400"}`} data-testid={`report-acc-${r.category}`}>{r.acc}</TableCell>
                  <TableCell className="py-2.5 text-right" data-testid={`report-diff-${r.category}`}>
                    {r.hasFark ? <FarkBadge diff={r.fark} /> : <span className="text-[11px] text-stone-400">—</span>}
                  </TableCell>
                  <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => toggleHidden("category", r.category, !r.hidden)}
                        title={r.hidden ? "Raporda göster" : "Rapordan gizle"}
                        data-testid={`report-hide-cat-${r.category}`}
                        className="border border-stone-300 bg-white p-1.5 text-stone-500 transition-colors hover:border-[#1C4E35] hover:text-[#1C4E35]"
                      >
                        {r.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    )}
                    <DetailActions
                      compact
                      pdfPath="/orders/by-product/visual-pdf"
                      pdfParams={{ scope, category: r.category, kind: kind !== ALL ? kind : undefined }}
                      emailPath="/orders/by-product/visual-email"
                      emailPayload={{ scope, category: r.category, kind: kind !== ALL ? kind : undefined }}
                      filename={`cop31_rapor_${r.category.replace(/\s+/g, "_")}_${ts}.pdf`}
                      testPrefix={`report-${r.category.replace(/\s+/g, "_")}`}
                      historyScope="byproduct-visual"
                      showMode={false}
                    />
                  </div>
                </TableCell>
                </TableRow>
                {isOpen && r.products.map((p) => {
                  const acc = p.acceptedQty || 0;
                  const diff = farkOf(p);
                  return (
                  <TableRow key={`${r.category}|${p.sku}|${p.variant || ""}`} className={`bg-stone-50/70 ${p.hidden ? "opacity-45" : ""}`} data-testid={`report-prod-row-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                    <TableCell className="py-1.5 pl-10">
                      <div className="flex items-center gap-2.5">
                        {p.image ? (
                          <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="h-10 w-10 shrink-0 border border-stone-200 bg-white object-contain" />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-stone-200 bg-white">
                            {p.kind === "service" ? <Wrench size={14} className="text-sky-500" /> : <Package size={14} className="text-stone-300" />}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#0A0D0B]">
                            {p.name}
                            {p.variant && <span className="ml-1.5 border border-stone-300 bg-white px-1 py-px text-[9px] text-stone-500">{p.variant}</span>}
                            {(p.manualQty || 0) > 0 && <span className="ml-1.5 border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title={`Manuel ekleme içerir (${p.manualQty} adet)`}>M</span>}
                            {p.kind === "service" && <span className="ml-1.5 border border-sky-300 bg-sky-50 px-1.5 py-px text-[9px] font-medium text-sky-700">Servis</span>}
                            {p.kind === "depo_disi" && <span className="ml-1.5 border border-orange-300 bg-orange-50 px-1.5 py-px text-[9px] font-medium text-orange-700">Depo Dışı</span>}
                            {p.hidden && <span className="ml-1.5 border border-stone-300 bg-stone-100 px-1.5 py-px text-[9px] font-medium text-stone-500">Raporda gizli</span>}
                          </p>
                          <p className="text-[10px] text-stone-400">{p.sku}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-[11px] text-stone-500">{p.orderCount} sipariş</TableCell>
                    <TableCell className={`py-1.5 text-right text-sm font-medium ${p.qty === 0 ? "text-stone-400" : ""}`}>{p.qty}</TableCell>
                    <TableCell className={`py-1.5 text-right text-sm font-medium ${(p.procuredQty || 0) > 0 ? "text-blue-700" : "text-stone-400"}`}>{p.procuredQty || 0}</TableCell>
                    <TableCell className={`py-1.5 text-right text-sm font-medium ${acc > 0 ? "text-green-700" : "text-stone-400"}`}>{acc}</TableCell>
                    <TableCell className="py-1.5 text-right">
                      <FarkBadge diff={diff} />
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => toggleHidden("product", p.sku, !p.hidden)}
                          title={p.hidden ? "Raporda göster" : "Rapordan gizle"}
                          data-testid={`report-hide-prod-${p.sku}`}
                          className="border border-stone-300 bg-white p-1 text-stone-500 transition-colors hover:border-[#1C4E35] hover:text-[#1C4E35]"
                        >
                          {p.hidden ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      <div className="border border-stone-200 bg-white p-4">
        <EmailHistory scope="byproduct-visual" testPrefix="report-history" />
      </div>
    </div>
  );
}
